import { LogoutIcon } from "@src/assets/icons/SidebarIcons/LogoutIcon";
import { SeparateIcon } from "@src/assets/icons/SidebarIcons/SeparateIcon";
import { StageFrontFullLogoIcon } from "@src/assets/icons/StageFrontFullLogoIcon";
import { StageFrontOnlyLogoIcon } from "@src/assets/icons/StageFrontOnlyLogoIcon";
import { useLocation, useNavigate } from "react-router-dom";
import { Item, Items } from "./Items";
import { LogoutContainer, MainContainer, ToggleButton, TopContainer } from "./Sidebar.styled";
import SidebarItem from "./SidebarItem";
import SidebarSection from "./SidebarSection";
import { Tooltip } from "react-tooltip";
import { paths } from "@src/routes/paths";
import { useAiConfidencePageEnabled } from "@src/hooks/useAiConfidencePageEnabled";
import { PipelineHealthWidget } from "@src/components/PipelineHealth/PipelineHealthWidget";

interface SidebarProps {
	isExpanded: boolean;
	isHidden: boolean;
	toggleExpanded: () => void;
}

const MAPPING_PATHS: Array<string> = [paths.home, paths.poMapping];
const REFERENCE_DATA_PATHS: Array<string> = [paths.reports, paths.venueAliases];
const MONITORING_PATHS: Array<string> = [paths.aiConfidence];

const Sidebar = ({ isExpanded, isHidden, toggleExpanded }: SidebarProps) => {
	const { pathname } = useLocation();
	const navigate = useNavigate();
	const isAiConfidenceEnabled = useAiConfidencePageEnabled();
	const visibleItems = Items.filter((item) => isAiConfidenceEnabled || item.navigateTo !== paths.aiConfidence);
	const mappingItems = visibleItems.filter((item) => MAPPING_PATHS.includes(item.navigateTo ?? ""));
	const referenceDataItems = visibleItems.filter((item) => REFERENCE_DATA_PATHS.includes(item.navigateTo ?? ""));
	const monitoringItems = visibleItems.filter((item) => MONITORING_PATHS.includes(item.navigateTo ?? ""));

	const handleActiveItem = (navigateTo?: string) => {
		if (!navigateTo) return false;
		if (navigateTo === "/") return pathname === "/";
		return pathname.startsWith(navigateTo);
	};

	const mapItemsToSidebar = (items: Array<Item>) => {
		return items.map((item) => {
			const Icon = item.icon;
			const navigateToRoute = () => (item.navigateTo ? navigate(item.navigateTo) : null);
			const isActive = handleActiveItem(item.navigateTo);

			const props = {
				isActive,
				Icon,
				navigateToRoute,
				isExpanded,
				...item,
			};

			return (
				<div
					key={item.label} // Add key here
					data-tooltip-content={item.label}
					data-tip={item.label}
					data-tooltip-id={"sidebar-tooltip"}
				>
					<SidebarItem {...props} />
					{!isExpanded && <Tooltip id={"sidebar-tooltip"} place={"right"} />}
				</div>
			);
		});
	};

	return (
		<MainContainer
			$isHidden={isHidden}
		>
			<TopContainer>{isExpanded ? <StageFrontFullLogoIcon /> : <StageFrontOnlyLogoIcon />}</TopContainer>
			<ToggleButton onClick={toggleExpanded}>
				<SeparateIcon />
			</ToggleButton>
			<SidebarSection headline="Mapping" isExpanded={isExpanded} isFirst>
				{mapItemsToSidebar(mappingItems)}
			</SidebarSection>
			<SidebarSection headline="Reference data" isExpanded={isExpanded}>
				{mapItemsToSidebar(referenceDataItems)}
			</SidebarSection>
			<SidebarSection headline="Monitoring" isExpanded={isExpanded}>
				{mapItemsToSidebar(monitoringItems)}
				<PipelineHealthWidget isExpanded={isExpanded} isHidden={isHidden} />
			</SidebarSection>
			<LogoutContainer>
				<LogoutIcon onClick={() => void 0}></LogoutIcon>
			</LogoutContainer>
		</MainContainer>
	);
};

export default Sidebar;
